import { ChangeDetectionStrategy, Component, HostBinding, Inject, Input, OnInit, Signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';

import { AccaBoostConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { RouteTag } from '@frontend/sports/common/core/data-access/route';
import { RouterEventsService } from '@frontend/sports/common/core/utils/router-events';
import { DialogAnimation, ModalDialogService, ModalRef } from '@frontend/sports/modal/feature';
import { DeviceService } from '@frontend/vanilla/core';
import { Store } from '@ngrx/store';

import { selectIsLinearBetslip } from '../../../../../base/store/selectors';
import { BetslipType } from '../../../../../core/betslip-type';
import { BetslipBarVisibilityService } from '../../../../betslip-bar/services/betslip-bar-visibility.service';
import { RewardTokensInfoComponent } from '../../../../reward-tokens/components/digital/reward-tokens-info.component';
import { RewardTokenContext } from '../../../../reward-tokens/reward-tokens.model';
import { ACCA_BOOST_LAYOUT_PROVIDER, AccaBoostLayoutProvider, INotification, NotificationType, isLadderNotification } from '../../../model';
import { AccaBoostNotificationService } from '../../../services/acca-boost-notification.service';
import { AccaBoostTrackingService } from '../../../services/digital/acca-boost-tracking.service';
import { BaseAccaBoostComponent } from '../../base-acca-boost.component';

@Component({
    selector: 'bs-acca-boost-notification',
    templateUrl: './acca-boost-notification.component.html',
    styleUrls: ['./acca-boost-notification.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class AccaBoostNotificationComponent extends BaseAccaBoostComponent implements OnInit {
    @Input() icon = '';
    @Input() notification: INotification | undefined;
    @Input() showAccaBoostIcon = false;
    @Input() usLadderEnabled: boolean;
    @Input() showInfoIcon = false;
    private tag: RouteTag;
    private isLinear: boolean;
    readonly isEnhancedLayoutEnabled: boolean;
    isAccaUpdatedInfoVisible = true;
    modalHandler: ModalRef | null = null;
    showDesktopAccaBoostInfo: boolean;
    betslipType = BetslipType.Combo;
    tokenContext: RewardTokenContext;
    accaBoostInfoIconSize = '12';
    accaBoostIconSize = '16';
    displayErrorIcon: boolean = false;
    private isEnabledDsl: Signal<boolean> = toSignal(this.betslipBarVisibilityService.enableDsl$, { initialValue: false });

    get isLadderNotification(): boolean {
        return isLadderNotification(this.notification);
    }

    @HostBinding('class') className = 'acca-boost-module acca-boost-notification acca-boost-notification--digital';
    @HostBinding('class.acca-boost-notification--betbar-enabled') get isBetSlipBarEnabled(): boolean | undefined {
        return this.isEnabledDsl();
    }

    @HostBinding('class.acca-boost-notification--desktop') get desktopClassName(): boolean {
        return this.isDesktop;
    }

    @HostBinding('class.acca-boost-notification-token--update-info') get notificationUpdateClassName(): boolean {
        return this.isAccaUpdatedInfoVisible && this.showAccaUpdatedInfo;
    }

    @HostBinding('class.acca-boost-notification--linear') get desktopLinearClassName(): boolean {
        // We only want the new design on mobile
        return this.isLinear && !this.isDesktop;
    }

    constructor(
        public sitecore: Sitecore,
        private notificationService: AccaBoostNotificationService,
        private trackingService: AccaBoostTrackingService,
        @Inject(ACCA_BOOST_LAYOUT_PROVIDER) layoutService: AccaBoostLayoutProvider,
        private routeService: RouterEventsService,
        store: Store,
        private dialogService: ModalDialogService,
        private deviceService: DeviceService,
        private betslipBarVisibilityService: BetslipBarVisibilityService,
        public accaBoostConfig: AccaBoostConfig,
    ) {
        super(layoutService);
        this.isEnhancedLayoutEnabled = this.accaBoostConfig.isEnhancedLayoutEnabled;
        this.routeService.currentActivationEnd.pipe(takeUntilDestroyed()).subscribe((route) => {
            if (route) {
                this.tag = route.snapshot.data.tag;
            }
        });

        store
            .select(selectIsLinearBetslip)
            .pipe(takeUntilDestroyed())
            .subscribe((isLinear) => (this.isLinear = isLinear));
    }

    ngOnInit(): void {
        if (isLadderNotification(this.notification)) {
            this.showAccaBoostIcon = false;
            this.trackingService.trackLadderInitialBubbleNotifications(this.tag, NotificationType.Ladder);
        } else if (this.notification?.type === NotificationType.OneSelectionAway) {
            this.trackingService.trackLadderInitialBubbleNotifications(this.tag, NotificationType.OneSelectionAway);
        }

        if (this.accaBoostConfig.accaBoostInfoVersion === 'V2') {
            this.accaBoostInfoIconSize = '16';
            this.accaBoostIconSize = '14';
        }

        if (this.accaBoostConfig.isEnhancedLayoutEnabled) {
            this.accaBoostIconSize = '20';
            this.displayErrorIcon = !!(this.notification && this.notification.type === NotificationType.Error);
        }
    }

    close(): void {
        if (isLadderNotification(this.notification)) {
            this.trackingService.trackLadderNotificationCloseClicked(this.notification.token.id);
        }

        this.notificationService.hideNotification();
    }

    onClickInfo(event: MouseEvent, isLadder: boolean): void {
        event.preventDefault();
        event.stopPropagation();
        this.tokenContext = { betslipType: this.betslipType };
        this.modalHandler = this.dialogService.openDialog(RewardTokensInfoComponent, {
            data: {
                inOverlay: false,
                modelDialog: true,
                tokenContext: this.tokenContext,
                showDesktopAccaBoostInfo: isLadder,
            },
            settings: {
                fit: false,
                showPageHeader: false,
                showDialogHeader: false,
                isModalDialog: true,
                animation: DialogAnimation.SlideInFromBottom,
                closeAnimation: DialogAnimation.SlideOutFromTop,
                cssClass: this.deviceService.isMobile ? 'acca-boost-ladder-info-icon-mobile' : 'acca-boost-ladder-info-icon-desktop',
            },
        });
    }

    get showAccaUpdatedInfo(): boolean {
        return isLadderNotification(this.notification) ? this.notification.tokenSwitched : false;
    }
}
